// The building's own grid lines for the G button, fetched on the first press
// (see ModelViewer.loadBuildingGrid and viewer setBuildingGridLoader).
//
// THE GRID IS THE PROJECT'S, NOT THE VISIBLE FILES'. The source document is
// root-04's answer (GET /glance/grid-source), chosen across EVERY published
// document. On SpaceX BA03 that is BA04 Core Shell - and the owner's "Model
// files" gear hides it. Fetching grids only for the shown documents then gave
// the viewer two grids, neither of them the named source, so it reported
// "ambiguous" and drew the generic lattice the moment a second file was
// switched on (owner, 2026-10-24). So: ask for the source FIRST, and fetch
// that document's grid file whether or not its model is shown. One fetch
// instead of one per document, too.
//
// Only when the source cannot be named (no answer, or a name with no grid
// file here) do we fall back to the shown documents' grids, and the viewer's
// own rule decides between them as before.
import { getGridSource } from "api/glance/gridSource";

const norm = (s) => String(s == null ? '' : s).trim().toUpperCase();

/**
 * @param {object}   o
 * @param {Array}    o.allSets      every document set (buildDocumentSets), hidden ones included
 * @param {Array}    o.shownSets    the sets the viewer loads (after the Model files gear)
 * @param {Function} o.getSource    () -> Promise<{ docName, how } | null>
 * @param {Function} o.read         (gridsPath) -> Promise<rows[]>
 * @returns {Promise<{ byDocument: Array<{ name, grids }>, source } | null>}
 */
export async function loadBuildingGridData({ allSets, shownSets, getSource, read }) {
  const fetchOne = async (set) => {
    try {
      const rows = await read(set.grids);
      return Array.isArray(rows) && rows.length ? { name: set.name, grids: rows } : null;
    } catch (e) {
      // A file that IS there and unreadable is worth knowing about; a
      // missing one (older exporter) never reaches here.
      console.warn(`[grid] ${set.name}: grid file present but could not be read`, e);
      return null;
    }
  };

  const source = await getSource();
  const want = norm(source && source.docName);
  if (want) {
    const set = (allSets || []).find((s) => s && s.grids && norm(s.name) === want);
    const got = set ? await fetchOne(set) : null;
    if (got) return { byDocument: [got], source };
  }

  const withGrids = (shownSets || []).filter((s) => s && s.grids);
  if (!withGrids.length) return null;
  const byDocument = (await Promise.all(withGrids.map(fetchOne))).filter(Boolean);
  return byDocument.length ? { byDocument, source } : null;
}

// EVERY OTHER 3D VIEW (tracking Model tab, timeline, asset viewer, issue
// canvas, create issue, socket viewer, align sheet, split search) registered
// no loader at all, so G there always drew the generic lattice. They do not
// list the project's files, so the server sends the chosen document's lines
// with its answer (grid-source?lines=1) and the view needs only its project.
export function projectGridLoader(projectId, getSource) {
  return async () => {
    const source = await getSource(projectId, { lines: true });
    if (!source || !source.docName || !Array.isArray(source.grids) || !source.grids.length) return null;
    const { grids, ...rest } = source;
    return { byDocument: [{ name: source.docName, grids }], source: rest };
  };
}

// Registered, not run: the viewer calls it on the first G press.
export function attachBuildingGrid(viewer, projectId, getSource = getGridSource) {
  const id = projectId && typeof projectId === "object" ? projectId._id : projectId;
  if (!viewer || typeof viewer.setBuildingGridLoader !== "function" || !id) return false;
  viewer.setBuildingGridLoader(projectGridLoader(String(id), getSource));
  return true;
}
